import axios from 'axios'
import { apiErrorDescription, createApiRequestId, normalizeApiRequestError } from './requestError'

// 请求重试配置
const MAX_RETRY_COUNT = 3 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟时间（毫秒）

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  export interface AxiosRequestConfig {
    retry?: boolean
    retryCount?: number
    fake?: boolean
  }
}

const api = axios.create({
  baseURL: (false && import.meta.env.VITE_ENABLE_PROXY) ? '/proxy/' : 'https://test.youpinzhi.net',
  timeout: 1000 * 60,
  responseType: 'json',
})

api.interceptors.request.use(
  (request) => {
    if (request.fake) {
      request.baseURL = '/fake/'
    }

    const appSettingsStore = useAppSettingsStore()
    const appAccountStore = useAppAccountStore()

    if (request.headers) {
      request.headers['Accept-Language'] = appSettingsStore.lang
      // Axios 重试沿用首次 request ID，便于把多次传输定位为同一次业务请求。
      if (!request.headers['X-Request-ID'] && !request.headers['x-request-id']) {
        request.headers['X-Request-ID'] = createApiRequestId()
      }
      if (appAccountStore.isLogin && !request.fake) {
        request.headers.Authorization = `Bearer ${appAccountStore.token}`
      }
    }

    return request
  },
)

/**
 * 统一处理未授权状态。
 *
 * 说明：
 * - 401 在这里已经触发登出/跳转
 * - 对调用方返回一个 pending promise，避免它继续以未捕获异常的形式打挂当前页面
 */
function handleUnauthorized() {
  useAppAccountStore().requestLogout()
  return new Promise<never>(() => {})
}

function handleError(error: any) {
  const normalized = normalizeApiRequestError(error)

  if (normalized.status === 401) {
    return handleUnauthorized()
  }
  else {
    useFaToast().error('Error', {
      description: apiErrorDescription(normalized),
    })
  }

  return Promise.reject(normalized)
}

api.interceptors.response.use(
  (response) => {
    const payload = response.data
    if (typeof payload !== 'object' || payload === null) {
      return Promise.reject(payload)
    }

    const code = Number(payload.code ?? 500)
    const normalized = normalizeApiRequestError({
      ...payload,
      status: response.status,
      response: {
        data: payload,
        headers: response.headers,
        status: response.status,
      },
      config: response.config,
    })

    if (code === 200) {
      return Promise.resolve(payload)
    }

    if (code === 401) {
      return handleUnauthorized()
    }
    else {
      useFaToast().error('Error', {
        description: apiErrorDescription(normalized),
      })
    }

    return Promise.reject(normalized)
  },
  async (error) => {
    const config = error.config
    if (!config || !config.retry) {
      return handleError(error)
    }

    config.retryCount = config.retryCount || 0
    if (config.retryCount >= MAX_RETRY_COUNT) {
      return handleError(error)
    }

    config.retryCount += 1
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))
    return api(config)
  },
)

export default api
